import { useState, useEffect } from 'react';
import { classroomAPI } from '../lib/api';
import { Star, CheckCircle2, Loader2 } from 'lucide-react';

const DIMENSIONS = [
  { key: 'primary_source_integrity', label: 'Primary Source Integrity', hint: 'Cited primary sources, not summaries of summaries.' },
  { key: 'contradiction_honesty', label: 'Contradiction Honesty', hint: 'Surfaced the contradicting source; did not hide it.' },
  { key: 'chain_coherence', label: 'Chain Coherence', hint: 'Five steps actually connect. Modern pattern anchored in earlier steps.' },
  { key: 'source_diversity', label: 'Source Diversity', hint: 'More than one perspective across the chain.' },
  { key: 'clarity_of_reasoning', label: 'Clarity of Reasoning', hint: 'Reader can follow without the submitter explaining.' },
];

function ScoreSlider({ dimension, value, onChange, readonly }) {
  return (
    <div className="space-y-1" data-testid={`rating-${dimension.key}`}>
      <div className="flex items-center justify-between">
        <label className="font-hud text-[10px] tracking-wider text-[#2F8A92] uppercase">{dimension.label}</label>
        <span className="font-mono text-xs text-[#D4A84A]">{value} / 5</span>
      </div>
      <div className="flex items-center gap-1">
        {[0, 1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            data-testid={`rating-${dimension.key}-${n}`}
            onClick={() => !readonly && onChange(n)}
            disabled={readonly}
            className={`flex-1 h-7 border rounded font-hud text-[10px] transition-all duration-300 ${
              n === value
                ? 'bg-[#D4A84A]/20 border-[#D4A84A] text-[#D4A84A]'
                : 'border-[#2F8A92]/15 text-[#52525B] hover:border-[#2F8A92]/40'
            } ${readonly ? 'cursor-default' : 'cursor-pointer'}`}
          >
            {n}
          </button>
        ))}
      </div>
      <p className="font-mono text-[9px] text-[#52525B]">{dimension.hint}</p>
    </div>
  );
}

export default function ChainKeeperRating({ submissionId, existingRating, onRated, readonly }) {
  const [scores, setScores] = useState({
    primary_source_integrity: 0,
    contradiction_honesty: 0,
    chain_coherence: 0,
    source_diversity: 0,
    clarity_of_reasoning: 0,
  });
  const [notes, setNotes] = useState('');
  const [visible, setVisible] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (existingRating) {
      setScores({
        primary_source_integrity: existingRating.primary_source_integrity || 0,
        contradiction_honesty: existingRating.contradiction_honesty || 0,
        chain_coherence: existingRating.chain_coherence || 0,
        source_diversity: existingRating.source_diversity || 0,
        clarity_of_reasoning: existingRating.clarity_of_reasoning || 0,
      });
      setNotes(existingRating.rater_notes || '');
      setVisible(existingRating.visible_to_submitter !== false);
    }
  }, [existingRating]);

  const total = Object.values(scores).reduce((a, b) => a + b, 0);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const r = await classroomAPI.rate(submissionId, {
        ...scores,
        rater_notes: notes,
        visible_to_submitter: visible,
      });
      onRated?.(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 border border-[#D4A84A]/20 rounded p-4" data-testid="chain-keeper-rating">
      <div className="flex items-center justify-between">
        <h4 className="font-hud text-sm tracking-widest uppercase text-[#D4A84A] flex items-center gap-2">
          <Star size={14} /> CHAIN KEEPER RATING
        </h4>
        <div className="text-right">
          <div className="font-historical text-xl text-[#D4A84A]">{total} / 25</div>
          {total >= 20 && <p className="font-mono text-[9px] text-[#2F8A92]">CURATED-SHELF READY</p>}
          {total >= 22 && <p className="font-mono text-[9px] text-[#D4A84A]">CHAIN KEEPER QUALITY</p>}
        </div>
      </div>

      <div className="space-y-3">
        {DIMENSIONS.map((d) => (
          <ScoreSlider
            key={d.key}
            dimension={d}
            value={scores[d.key]}
            onChange={(v) => setScores({ ...scores, [d.key]: v })}
            readonly={readonly}
          />
        ))}
      </div>

      {!readonly && (
        <>
          <textarea
            data-testid="rater-notes"
            className="hud-input text-xs min-h-[80px] resize-y"
            placeholder="Notes for the submitter (optional)…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <label className="flex items-center gap-2 font-mono text-[10px] text-white/80">
            <input
              type="checkbox"
              data-testid="visible-toggle"
              checked={visible}
              onChange={(e) => setVisible(e.target.checked)}
            />
            Share notes with submitter
          </label>
          {error && <p className="font-mono text-[10px] text-red-400">{error}</p>}
          <button
            data-testid="submit-rating-btn"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full font-hud text-xs tracking-wider text-[#0A0A0A] bg-[#D4A84A] px-3 py-2 hover:bg-[#D4A84A]/80 disabled:opacity-50 transition-all duration-300 flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
            {existingRating ? 'UPDATE RATING' : 'SUBMIT RATING'}
          </button>
        </>
      )}

      {readonly && existingRating && (
        <p className="font-mono text-[10px] text-[#52525B] text-center">
          Rated by {existingRating.rater_name} · {existingRating.rater_role}
        </p>
      )}
    </div>
  );
}
